import { useCurrentUser } from '@/hooks/dataHelpers';
import { logRedactedError } from '@/lib/telemetry/logError';
import { useAuthStore } from '@/store';
import { useCustomMutation, useNotification } from '@refinedev/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

export interface SwitchIdentityTarget {
  /** Tracked as the pending key — falls back to email when no id exists (demo personas). */
  id?: string | number;
  email?: string;
}

/**
 * v3 handleSwitchIdentity parity (UserListItemActions): superadmin/support
 * logs in AS another user — response carries a fresh token + user; auth
 * store swap + hard navigation home resets all cached filters. Shared by
 * UsersPage (login-as) and the demo account switcher (DemoBar) — same wire,
 * must not drift between the two callers.
 */
export const useSwitchIdentity = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const { open } = useNotification();
  const { mutate } = useCustomMutation();
  const [pendingId, setPendingId] = useState<string | number | null>(null);

  const switchIdentity = (target: SwitchIdentityTarget) => {
    if (!target.email) return;
    const key = target.id ?? target.email;
    setPendingId(key);
    mutate(
      { url: 'switch_identity.json', method: 'post', values: { email: target.email } },
      {
        onSuccess: (response: any) => {
          setPendingId(null);
          const attributes = response?.data?.user?.data?.attributes;
          if (!attributes) {
            open?.({ type: 'error', message: t('UserListItemActions.failed_to_switch_identity') });
            return;
          }
          useAuthStore.getState().setAuthState({
            token: response.data.token,
            user: attributes,
            isAuthenticated: true,
            loading: false,
            error: null,
            originalUserRole: currentUser.role,
          });
          open?.({ type: 'success', message: t('UserListItemActions.logged_in_successfully') });
          navigate('/');
        },
        onError: () => {
          setPendingId(null);
          open?.({ type: 'error', message: t('UserListItemActions.failed_to_switch_identity') });
        },
      }
    );
  };

  /** v3 SiderFooter.handleMenuClick parity — switch between the SAME person's
   *  org_users: `{ user_uuid }` payload, store reset BEFORE the new auth state
   *  (v3 awaits `logout()` first), and NO `originalUserRole` — an org switch
   *  is not impersonation. Unlike `switchIdentity` this ends in a FULL PAGE
   *  LOAD (dev directive, 31-Jul): permissions are per-org and a fresh boot is
   *  deterministic where an SPA remount is only as good as its cache seams. */
  const switchOrgUser = (target: { uuid: string }) => {
    if (!target.uuid) return;
    setPendingId(target.uuid);
    mutate(
      { url: 'switch_identity.json', method: 'post', values: { user_uuid: target.uuid } },
      {
        onSuccess: async (response: any) => {
          setPendingId(null);
          const attributes = response?.data?.user?.data?.attributes;
          if (!attributes) {
            open?.({ type: 'error', message: t('UserListItemActions.failed_to_switch_identity') });
            return;
          }
          await useAuthStore.getState().logout();
          useAuthStore.getState().setAuthState({
            token: response.data.token,
            user: attributes,
            isAuthenticated: true,
            loading: false,
            error: null,
          });
          // The persist middleware writes asynchronously, so a top-level nav can
          // outrun it and reload with no token (= logged out). Flush FIRST, then
          // leave. A failed flush still navigates: landing logged out is
          // recoverable, staying on a poisoned page is not.
          try {
            await useAuthStore.getState().flushToPersistence();
          } catch (error) {
            logRedactedError('switchOrgUser: auth flush before reload failed', error);
          }
          // Target '/' (not the current route) — permissions differ per org and
          // '/' resolves the correct default page per role. The reload is what
          // makes an org switch safe: the SPA path poisoned the permission cache
          // (logout() nulls the user and clears the cache, so useCan refetches
          // deny-all before the new org lands) and the Refine remount key can't
          // catch it, because an org switch keeps the SAME user id — see the
          // corrected wire pin in useSwitchIdentity.test.tsx. Do NOT "optimise"
          // this back to an SPA navigate().
          window.location.assign('/');
        },
        onError: () => {
          setPendingId(null);
          open?.({ type: 'error', message: t('UserListItemActions.failed_to_switch_identity') });
        },
      }
    );
  };

  return { switchIdentity, switchOrgUser, pendingId };
};
